import { useExperiments } from '../context/ExperimentsContext';
import { isBrowser } from '../utils/isBrowser';

// Helper function to parse the variation from URL (can be memoized if needed, but often fast enough)
// Moved outside the hook for better separation and testability.
function getUrlVariationValue(paramName: string): string | null {
  // Guard against SSR environments where window is not available
  if (!isBrowser || !window.location || !window.location.search) {
    return null;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    // Get value, trim whitespace, convert to lowercase for case-insensitive comparison
    const value = params.get(paramName)?.trim().toLowerCase();

    switch (value) {
      case 'a':
        return 'variationA';
      case 'b':
        return 'variationB';
      case 'c':
        return 'variationC';
      default:
        // Return null if param is missing, empty, or not a recognised value
        return null;
    }
  } catch (error) {
    // Log potential errors during URL parsing, although unlikely for search params
    console.error(`Error parsing URL parameter "${paramName}":`, error);
    return null;
  }
}

/**
 * Custom hook for determining the active variation for an A/B test.
 * It checks URL parameters first, then falls back to experiment flags from context.
 * Designed to be SSR-safe: uses the context value on the server and determines
 * the effective variation (considering URL params) immediately on the client's initial render.
 *
 * @param experimentId - The unique identifier for the experiment (e.g., 'KFH25002').
 * @param paramName - Optional name of the URL query parameter used for forcing variations (defaults to 'variation').
 *                    Expected values: 'a', 'b', 'c' (case-insensitive).
 * @returns An object containing the effective variation and boolean flags.
 */
export const useVariation = (experimentId: string, paramName = 'variation') => {
  // 1. Get experiment value from context
  const { experiments } = useExperiments() || {}; // Default to empty object if context is null/undefined
  // Use optional chaining and nullish coalescing for safety
  const experimentContextValue = experiments?.[experimentId] ?? null;

  // 2. Get variation value from URL query parameter (client-side only)
  // This calculation happens *during* render. On the server, it returns null.
  // On the client's initial render, it reads the URL.
  const urlVariationValue = getUrlVariationValue(paramName);

  // 3. Determine the effective variation (URL parameter takes precedence)
  // On SSR, urlVariationValue will be null, so experimentContextValue is used.
  // On CSR (initial render), urlVariationValue (if present and valid) overrides experimentContextValue.
  // Subsequent re-renders on the client will consistently use the same logic.
  const effectiveVariation = urlVariationValue ?? experimentContextValue;

  // 4. Derive boolean flags for convenience
  const isTestVariationA = effectiveVariation === 'variationA';
  const isTestVariationB = effectiveVariation === 'variationB';
  const isTestVariationC = effectiveVariation === 'variationC';
  // A flag to check if *any* known test variation is active (A, B, or C)
  const isAnyTestVariation = isTestVariationA || isTestVariationB || isTestVariationC;

  return {
    /** The determined variation ('variationA', 'variationB', 'variationC', etc.) or null. */
    effectiveVariation: effectiveVariation as string | null, // Type assertion might be needed depending on context type
    /** True if the effective variation is 'variationA'. */
    isTestVariationA,
    /** True if the effective variation is 'variationB'. */
    isTestVariationB,
    /** True if the effective variation is 'variationC'. */
    isTestVariationC,
    /** True if the effective variation is one of the known variations (A, B, C). */
    isAnyTestVariation, // Renamed from isAnyTestVariation for potentially broader meaning
  };
};
